import React, { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { format } from 'date-fns'
import { CurrentProjectManagerContext } from '../../../contexts/CurrentProjectManagerContext'
import axiosService from '../../../utils/AxiosService'
import Button from '../../Shared/Button'
import AuditLogFilters from './AuditLogParts/AuditLogFilters'
import AuditLogEntry from './AuditLogParts/AuditLogEntry'
import { TABLE_COLUMNS } from './auditLogConstants'

const EMPTY_FILTERS = {
  campaignId: null,
  itemType: null,
  event: null,
  whodunnitId: null,
  itemId: null,
  dateFrom: null,
  dateTo: null,
}

export default function AuditLog() {
  const { currentProjectManager } = useContext(CurrentProjectManagerContext)
  const navigate = useNavigate()

  const [projectManagerOptions, setProjectManagerOptions] = useState([])
  const [campaignOptions, setCampaignOptions] = useState([])
  const [filters, setFilters] = useState(EMPTY_FILTERS)
  const [entries, setEntries] = useState([])
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!currentProjectManager?.id) return
    if (!currentProjectManager.is_admin) {
      navigate('/project_manager/campaigns')
      return
    }

    axiosService.get('/api/project_manager/project_managers')
      .then(managers => setProjectManagerOptions(managers.map(pm => ({
        value: pm.id,
        label: [pm.first_name, pm.last_name].filter(Boolean).join(' ') || pm.email,
      }))))
      .catch(resp => console.log(resp))

    axiosService.get('/api/project_manager/campaigns')
      .then(campaigns => setCampaignOptions(campaigns.map(campaign => ({
        value: campaign.id,
        label: campaign.name,
      }))))
      .catch(resp => console.log(resp))
  }, [currentProjectManager?.id])

  useEffect(() => {
    if (!currentProjectManager?.is_admin) return

    const timeout = setTimeout(() => fetchEntries(true), 300)
    return () => clearTimeout(timeout)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProjectManager?.is_admin, JSON.stringify(filters)])

  const buildParams = (beforeId) => {
    const params = {}
    if (filters.campaignId) params.campaign_id = filters.campaignId
    if (filters.itemType) params.item_type = filters.itemType
    if (filters.event) params.event = filters.event
    if (filters.whodunnitId) params.whodunnit_id = filters.whodunnitId
    if (filters.itemId) params.item_id = filters.itemId
    if (filters.dateFrom) params.date_from = format(filters.dateFrom, 'yyyy-MM-dd')
    if (filters.dateTo) params.date_to = format(filters.dateTo, 'yyyy-MM-dd')
    if (beforeId) params.before_id = beforeId
    return params
  }

  const fetchEntries = (reset) => {
    setLoading(true)
    axiosService.get('/api/project_manager/audit_log', { params: buildParams(reset ? null : entries[entries.length - 1]?.id) })
      .then(({ entries: newEntries, has_more }) => {
        setEntries(reset ? newEntries : [...entries, ...newEntries])
        setHasMore(has_more)
      })
      .catch(resp => console.log(resp))
      .finally(() => setLoading(false))
  }

  return (
    <div className="pt-12 bg-white dark:bg-rb-dark-mode min-h-screen">
      <div className="flex items-center justify-between w-full h-20 px-24 align-center bg-gradient-to-br from-rb-green from-30% via-teal-500 via-60% to-rb-green to-90%">
        <p className="text-white text-2xl">
          Audit Log
        </p>
      </div>

      <div className="px-24 pt-8">
        <AuditLogFilters
          filters={filters}
          onChange={setFilters}
          onReset={() => setFilters(EMPTY_FILTERS)}
          projectManagerOptions={projectManagerOptions}
          campaignOptions={campaignOptions}
        />

        <div className="mt-8 rounded-lg border-1 border-rb-widget-border dark:border-rb-dark-mode-lighter overflow-hidden">
          <div className="flex items-center gap-4 px-4 py-2 bg-rb-grey dark:bg-rb-dark-mode-lighter text-xs font-semibold uppercase text-rb-mat-grey dark:text-rb-dark-mode-text-grey">
            <span className={TABLE_COLUMNS.chevron} />
            <span className={TABLE_COLUMNS.when}>When</span>
            <span className={TABLE_COLUMNS.event}>Event</span>
            <span className={TABLE_COLUMNS.model}>Model</span>
            <span className={TABLE_COLUMNS.record}>Record</span>
            <span className={`${TABLE_COLUMNS.changedBy} text-right`}>Changed by</span>
          </div>

          {entries.length === 0 && !loading && (
            <p className="px-4 py-8 text-center text-sm text-rb-mat-grey dark:text-rb-dark-mode-text-grey">
              No changes found for the current filters.
            </p>
          )}

          {entries.map(entry => <AuditLogEntry key={entry.id} entry={entry} />)}
        </div>

        {hasMore && (
          <div className="flex justify-center mt-6">
            <Button variant="secondary" onClick={() => fetchEntries(false)} disabled={loading}>
              {loading ? 'Loading…' : 'Load more'}
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
